import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, ListChecks, MapPin, PauseCircle, PlayCircle, Plus, RefreshCw, Search, Trash2, Users } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import ConfirmDialog from '../ConfirmDialog';
import ModuloDipendente, { ModoModulo, ValoriModulo } from './ModuloDipendente';
import CredenzialiUnaVolta from './CredenzialiUnaVolta';
import DialogoCambiaSede from './DialogoCambiaSede';
import VideoGuidaDipendenti from './VideoGuidaDipendenti';
import {
  CredenzialiDipendente,
  DipendenteStudio,
  ElencoDipendenti,
  ErroreDipendenti,
  aggiornaSezioniDipendente,
  cambiaSedeDipendente,
  creaDipendente,
  eliminaDipendente,
  getDipendenti,
  reimpostaPasswordDipendente,
  riattivaDipendente,
  salvaImpostazioniDipendenti,
  sospendiDipendente,
} from '../../services/dipendentiService';
import { CodiceSezione, ETICHETTE_SEZIONE, sessioneInImpersonificazione } from '../../utils/sezioniDipendente';
import { conPiuSedi, filtraDipendenti, raggruppaPerSede, valoreSede } from '../../utils/sediDipendenti';

type Stato = 'caricamento' | 'nascosto' | 'errore' | 'pronto';

// Oltre questo numero di dipendenti compare la ricerca anche con una sola sede
const SOGLIA_RICERCA = 10;
const TUTTE_LE_SEDI = '*';

const contaDipendenti = (n: number) => (n === 1 ? '1 dipendente' : `${n} dipendenti`);

const nominativoDi = (d: DipendenteStudio) => `${d.nome} ${d.cognome}`.trim();

const ultimoAccesso = (quando?: string | null): string => {
  if (!quando) return 'Mai entrato';
  const data = new Date(quando);
  if (Number.isNaN(data.getTime())) return 'Mai entrato';
  return `Ultimo accesso il ${data.toLocaleDateString('it-IT')} alle ${data.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
};

const messaggioDi = (err: unknown, ripiego: string): string =>
  (err instanceof Error && err.message) ? err.message : ripiego;

/**
 * Card «I miei dipendenti» nella scheda profilo del titolare (spec dipendenti §7).
 * Con più sedi i dipendenti sono divisi per sede (prima la principale), con ricerca, filtro e «Cambia sede».
 * Il limite dei dipendenti è interno: il titolare non lo vede mai, al 409 compare il messaggio del server.
 * Non compare se la funzione non è attiva per lo studio (403 `DIPENDENTI_SPENTI`) o se il server
 * non ha la gestione dei dipendenti (altri 4xx): in quei casi nessun messaggio d'errore.
 */
const GestioneDipendenti: React.FC = () => {
  const toast = useToast();
  const [stato, setStato] = useState<Stato>('caricamento');
  const [dati, setDati] = useState<ElencoDipendenti | null>(null);
  const [modulo, setModulo] = useState<{ modo: ModoModulo; dipendente?: DipendenteStudio } | null>(null);
  const [credenziali, setCredenziali] = useState<CredenzialiDipendente | null>(null);
  const [daEliminare, setDaEliminare] = useState<DipendenteStudio | null>(null);
  const [daSospendere, setDaSospendere] = useState<DipendenteStudio | null>(null);
  const [daSpostare, setDaSpostare] = useState<DipendenteStudio | null>(null);
  const [ricerca, setRicerca] = useState('');
  const [filtroSede, setFiltroSede] = useState(TUTTE_LE_SEDI);
  // «Accedi come associato»: sola lettura (il server risponde comunque 403 IMPERSONIFICAZIONE)
  const [soloLettura] = useState(sessioneInImpersonificazione);
  const [inCorso, setInCorso] = useState<string | null>(null);
  const [sondaggi, setSondaggi] = useState(false);
  const [salvataggioSondaggi, setSalvataggioSondaggi] = useState(false);

  const carica = useCallback(async () => {
    try {
      const elenco = await getDipendenti();
      setDati(elenco);
      setSondaggi(!!elenco?.impostazioni?.sondaggiRispondonoDipendenti);
      setStato('pronto');
    } catch (err) {
      const status = err instanceof ErroreDipendenti ? err.status : undefined;
      // 403 DIPENDENTI_SPENTI (funzione non attiva) e ogni altro 4xx: la card non c'è
      setStato(status && status >= 400 && status < 500 ? 'nascosto' : 'errore');
    }
  }, []);

  useEffect(() => {
    carica();
  }, [carica]);

  if (stato === 'nascosto') return null;

  const dipendenti = dati?.dipendenti || [];
  const sedi = dati?.sedi || [];
  const gruppiTutti = raggruppaPerSede(dipendenti, sedi);
  const piuSedi = conPiuSedi(sedi, gruppiTutti);
  const conFiltri = dipendenti.length > SOGLIA_RICERCA || piuSedi;
  // sedi del filtro: quelle dello studio più quelle che compaiono solo nei dipendenti
  const opzioniSede = [...sedi, ...gruppiTutti.map((g) => g.sede)]
    .filter((s, i, tutte) => tutte.findIndex((x) => valoreSede(x.id) === valoreSede(s.id)) === i);
  const filtrati = filtraDipendenti(dipendenti, ricerca)
    .filter((d) => !piuSedi || filtroSede === TUTTE_LE_SEDI || valoreSede(d.sede?.id) === filtroSede);
  const gruppi = raggruppaPerSede(filtrati, sedi);

  const conferma = async (valori: ValoriModulo) => {
    if (!modulo) return;
    try {
      if (modulo.modo === 'nuovo') {
        const res = await creaDipendente({
          nome: valori.nome || '',
          cognome: valori.cognome || '',
          password: valori.password || '',
          sezioni: valori.sezioni,
          ...(valori.sede !== undefined ? { sede: valori.sede } : {}),
        });
        setModulo(null);
        setCredenziali(res.credenziali);
        toast.success('Accesso creato');
      } else if (modulo.modo === 'sezioni' && modulo.dipendente) {
        await aggiornaSezioniDipendente(modulo.dipendente.id, valori.sezioni || []);
        setModulo(null);
        toast.success('Sezioni aggiornate');
      } else if (modulo.modo === 'password' && modulo.dipendente) {
        const res = await reimpostaPasswordDipendente(modulo.dipendente.id, valori.password || '');
        setModulo(null);
        setCredenziali(res.credenziali);
        toast.success('Password reimpostata');
      }
    } catch (err) {
      toast.error(messaggioDi(err, 'Operazione non riuscita'));
      throw err;
    }
    carica();
  };

  const azione = async (d: DipendenteStudio, chiamata: (id: string) => Promise<unknown>, riuscita: string) => {
    setInCorso(d.id);
    try {
      await chiamata(d.id);
      toast.success(riuscita);
      await carica();
    } catch (err) {
      toast.error(messaggioDi(err, 'Operazione non riuscita'));
    } finally {
      setInCorso(null);
    }
  };

  const confermaEliminazione = async () => {
    const d = daEliminare;
    setDaEliminare(null);
    if (d) await azione(d, eliminaDipendente, 'Accesso eliminato');
  };

  const confermaSospensione = async () => {
    const d = daSospendere;
    setDaSospendere(null);
    if (d) await azione(d, sospendiDipendente, 'Accesso sospeso');
  };

  const confermaSpostamento = (d: DipendenteStudio, sede: string | null) => {
    setDaSpostare(null);
    azione(d, (id) => cambiaSedeDipendente(id, sede), 'Sede aggiornata');
  };

  const cambiaSondaggi = async () => {
    const prima = sondaggi;
    const dopo = !prima;
    setSondaggi(dopo);
    setSalvataggioSondaggi(true);
    try {
      await salvaImpostazioniDipendenti({ sondaggiRispondonoDipendenti: dopo });
      toast.success(dopo ? 'Ai sondaggi possono rispondere anche i dipendenti' : 'Ai sondaggi rispondi solo tu');
    } catch (err) {
      setSondaggi(prima);
      toast.error(messaggioDi(err, 'Impostazione non salvata'));
    } finally {
      setSalvataggioSondaggi(false);
    }
  };

  const pulsanteRiga = 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50';

  const rigaDipendente = (d: DipendenteStudio) => {
    const modificabile = d.modificabile !== false;
    const sospeso = d.stato === 'sospeso';
    const occupato = inCorso === d.id;
    return (
      <li key={d.id} data-testid={`dipendente-${d.id}`} className="p-3 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{nominativoDi(d)}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sospeso
                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'}`}
              >
                {sospeso ? 'Sospeso' : 'Attivo'}
              </span>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{ultimoAccesso(d.lastLoginAt)}</p>
          </div>
          {soloLettura ? null : modificabile ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              <button type="button" disabled={occupato} onClick={() => setModulo({ modo: 'sezioni', dipendente: d })} className={pulsanteRiga}>
                <ListChecks className="w-3.5 h-3.5" aria-hidden="true" />
                Sezioni
              </button>
              {sedi.length > 1 && (
                <button type="button" disabled={occupato} onClick={() => setDaSpostare(d)} className={pulsanteRiga}>
                  <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                  Cambia sede
                </button>
              )}
              <button type="button" disabled={occupato} onClick={() => setModulo({ modo: 'password', dipendente: d })} className={pulsanteRiga}>
                <KeyRound className="w-3.5 h-3.5" aria-hidden="true" />
                Reimposta password
              </button>
              {sospeso ? (
                <button type="button" disabled={occupato} onClick={() => azione(d, riattivaDipendente, 'Accesso riattivato')} className={pulsanteRiga}>
                  <PlayCircle className="w-3.5 h-3.5" aria-hidden="true" />
                  Riattiva
                </button>
              ) : (
                <button type="button" disabled={occupato} onClick={() => setDaSospendere(d)} className={pulsanteRiga}>
                  <PauseCircle className="w-3.5 h-3.5" aria-hidden="true" />
                  Sospendi
                </button>
              )}
              <button
                type="button"
                disabled={occupato}
                onClick={() => setDaEliminare(d)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                Elimina
              </button>
            </div>
          ) : (
            <span className="text-xs text-gray-400 dark:text-gray-500">Gestito dall'altra sede</span>
          )}
        </div>
        {d.sezioni.length > 0 ? (
          <div className="flex flex-wrap gap-1 mt-2">
            {d.sezioni.map((s) => (
              <span key={s} className="px-2 py-0.5 rounded-md text-xs bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300">
                {ETICHETTE_SEZIONE[s as CodiceSezione] || s}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Nessuna sezione abilitata</p>
        )}
      </li>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-6 transition-shadow duration-200 hover:shadow-xl">
      <div className="flex items-start gap-3 mb-4 flex-wrap">
        <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md shadow-teal-500/20">
          <Users className="w-5 h-5 text-white" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">I miei dipendenti</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Un accesso personale per chi lavora nello studio: scegli tu le sezioni che può usare.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <VideoGuidaDipendenti />
          {stato === 'pronto' && !soloLettura && (
            <button
              type="button"
              onClick={() => setModulo({ modo: 'nuovo' })}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-3.5 h-3.5" aria-hidden="true" />
              Aggiungi dipendente
            </button>
          )}
        </div>
      </div>

      {stato === 'caricamento' && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Caricamento…</p>
      )}

      {stato === 'errore' && (
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          <span>Impossibile caricare i dipendenti.</span>
          <button type="button" onClick={() => { setStato('caricamento'); carica(); }} className="inline-flex items-center gap-1 text-xs font-medium hover:underline">
            <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
            Riprova
          </button>
        </div>
      )}

      {stato === 'pronto' && (
        <>
          {soloLettura && (
            <p role="note" className="p-3 mb-4 rounded-lg text-sm bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300">
              In modalità "Accedi come associato" la gestione dei dipendenti è in sola lettura
            </p>
          )}
          <div className="flex items-center justify-between gap-3 p-3 mb-4 rounded-lg bg-gray-50 dark:bg-gray-800">
            <div className="min-w-0">
              <p id="dipendenti-sondaggi" className="text-sm font-medium text-gray-900 dark:text-white">Sondaggi: possono rispondere anche i dipendenti</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {sondaggi
                  ? 'Chi ha la sezione Sondaggi può compilarli. La risposta resta una per studio.'
                  : 'Rispondi solo tu: i dipendenti vedono i sondaggi ma non li compilano.'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={sondaggi}
              aria-labelledby="dipendenti-sondaggi"
              onClick={cambiaSondaggi}
              disabled={salvataggioSondaggi || soloLettura}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${sondaggi ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${sondaggi ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {dipendenti.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Non hai ancora creato accessi per i tuoi dipendenti.</p>
          ) : (
            <>
              {conFiltri && (
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                    <input
                      type="search"
                      value={ricerca}
                      onChange={(e) => setRicerca(e.target.value)}
                      aria-label="Cerca per nome o cognome"
                      placeholder="Cerca per nome o cognome"
                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>
                  {piuSedi && (
                    <select
                      value={filtroSede}
                      onChange={(e) => setFiltroSede(e.target.value)}
                      aria-label="Filtra per sede"
                      className="sm:w-64 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                    >
                      <option value={TUTTE_LE_SEDI}>Tutte le sedi</option>
                      {opzioniSede.map((s) => (
                        <option key={valoreSede(s.id)} value={valoreSede(s.id)}>{s.nome}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
              {filtrati.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Nessun dipendente corrisponde alla ricerca.</p>
              ) : piuSedi ? (
                <div className="space-y-4">
                  {gruppi.map((g) => {
                    const chiave = g.sede.id || 'principale';
                    return (
                      <section key={chiave} data-testid={`gruppo-sede-${chiave}`} aria-labelledby={`gruppo-sede-titolo-${chiave}`}>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <h3 id={`gruppo-sede-titolo-${chiave}`} className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 dark:text-gray-100 min-w-0">
                            <MapPin className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400 flex-shrink-0" aria-hidden="true" />
                            <span className="truncate">{g.sede.nome}</span>
                          </h3>
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 flex-shrink-0">
                            {contaDipendenti(g.dipendenti.length)}
                          </span>
                        </div>
                        <ul className="space-y-2">{g.dipendenti.map(rigaDipendente)}</ul>
                      </section>
                    );
                  })}
                </div>
              ) : (
                <ul className="space-y-2">{filtrati.map(rigaDipendente)}</ul>
              )}
            </>
          )}
        </>
      )}

      {modulo && (
        <ModuloDipendente
          modo={modulo.modo}
          dipendente={modulo.dipendente}
          sezioniDisponibili={dati?.sezioniDisponibili}
          sedi={sedi}
          onClose={() => setModulo(null)}
          onConferma={conferma}
        />
      )}

      {credenziali && (
        <CredenzialiUnaVolta credenziali={credenziali} onClose={() => setCredenziali(null)} />
      )}

      <DialogoCambiaSede
        dipendente={daSpostare}
        sedi={sedi}
        onClose={() => setDaSpostare(null)}
        onConferma={confermaSpostamento}
      />

      <ConfirmDialog
        isOpen={!!daSospendere}
        onClose={() => setDaSospendere(null)}
        onConfirm={confermaSospensione}
        type="warning"
        title={daSospendere ? `Sospendere l'accesso di ${nominativoDi(daSospendere)}?` : ''}
        message="Il dipendente esce subito dal sito e dall'app e non può più entrare finché non lo riattivi. Le sue sezioni restano salvate."
        confirmText="Sospendi accesso"
      />

      <ConfirmDialog
        isOpen={!!daEliminare}
        onClose={() => setDaEliminare(null)}
        onConfirm={confermaEliminazione}
        type="danger"
        title={daEliminare ? `Eliminare l'accesso di ${nominativoDi(daEliminare)}?` : ''}
        message="Il dipendente non potrà più entrare e le sue sessioni si chiudono subito. Il lavoro già fatto resta nello studio."
        confirmText="Elimina accesso"
      />
    </div>
  );
};

export default GestioneDipendenti;
